import { CompanySettings, WebAppTrackingMode } from 'src/models';
import {
  UpsellCtaStrategy,
  UpsellPresentation,
  UpsellUpgradePlan,
  UpsellVariant
} from './upsell-page/upsell-page.types';

export interface UpsellConfig {
  /** URL segment used in /upgrade/:name */
  name: string;
  /** Segment tracking event name: 'Benchmarks AI', 'Video Screen Recording', etc. */
  trackingName: string;
  /** GrowthBook feature flag key: 'benchmark:upsell', 'payroll-upsell', etc. */
  featureFlagKey: string;
  /** GrowthBook addons-list linkname for exact lookup: 'BenchmarksAI', 'UAR', etc. */
  addonLinkname?: string;
  /**
   * Normalized linkname for fuzzy lookup when the exact GrowthBook linkname is unknown.
   * Normalization: lowercase + strip non-alphanumeric (e.g. "Software Cost Insights" → "softwarecostinsights").
   * Used only when addonLinkname is not set.
   */
  addonLinknameNormalized?: string;
  /** Legacy subscription key substrings for addon detection */
  legacySubscriptionKeys?: string[];
  /**
   * Extra billing plan product IDs (Zuora / lineItems) that still mean this add-on is purchased.
   * Use when GrowthBook `addonIds` and billing historically used different strings (e.g. dev vs prod catalogs).
   */
  additionalBillingPlanIdsForAddonMatch?: readonly string[];
  /** Route to navigate back to after purchase */
  reportRoute: string;
  /** Post-upgrade setup checklist route (when applicable) */
  setupRoute?: string;
  /** Custom flag evaluator for flags that return objects instead of booleans */
  featureFlagEnabled?: (value: any) => boolean;
  /** Shared standard upsell page vs legacy custom component */
  variant?: UpsellVariant;
  /** Copy, assets, and tracking for standard-self-serve upsells */
  presentation?: UpsellPresentation;
  /** CTA layout strategy; defaults to learnMoreAndUpgradeCtaStrategy */
  ctaStrategy?: UpsellCtaStrategy;
  /** Billing plan target for the Upgrade CTA; defaults to standard */
  upgradePlan?: UpsellUpgradePlan;
  /** Company settings to enable when upgrading from this upsell (e.g. trackConnectivity) */
  postUpgradeCompanySettings?: Partial<CompanySettings>;
}

/** Known OVR add-on plan IDs across GrowthBook / billing environments (union for purchase checks). */
export const OFFICE_VS_REMOTE_ADDITIONAL_BILLING_PLAN_IDS: readonly string[] = [
  'office_vs_remote_report_addons_monthly',
  'office_vs_remote_report_addons_quarterly',
  'office_vs_remote_report_addons_semi_yearly',
  'office_vs_remote_report_addons_yearly',
  'officevsremove_report_add_on_monthly',
  'officevsremove_report_add_on_quarterly',
  'officevsremove_report_add_on_semi_yearly',
  'officevsremove_report_add_yearly',
];

export const UPSELL_REGISTRY = new Map<string, UpsellConfig>([
  ['benchmarks', {
    name: 'benchmarks',
    trackingName: 'Benchmarks AI',
    featureFlagKey: 'benchmark:upsell',
    addonLinkname: 'BenchmarksAI',
    legacySubscriptionKeys: ['benchmarks_add-on'],
    reportRoute: '/benchmarks',
  }],
  ['unusual-activity', {
    name: 'unusual-activity',
    trackingName: 'Unusual Activity Report',
    featureFlagKey: 'unusual-activity-upsell',
    addonLinkname: 'UAR',
    legacySubscriptionKeys: ['uar_add_on'],
    reportRoute: '/unusual-activity',
  }],
  ['office-vs-remote', {
    name: 'office-vs-remote',
    trackingName: 'Office Vs Remote',
    featureFlagKey: 'office-vs-remote:upsell',
    addonLinkname: 'Office VS Remote report',
    additionalBillingPlanIdsForAddonMatch: OFFICE_VS_REMOTE_ADDITIONAL_BILLING_PLAN_IDS,
    legacySubscriptionKeys: [
      'office_vs_remote_report',
      'officevsremove_report',
      'office_vs_remote_report_addons',
    ],
    reportRoute: '/office-vs-remote',
  }],
  ['software-cost-insights', {
    name: 'software-cost-insights',
    trackingName: 'Software Cost Insight',
    featureFlagKey: 'software-cost-insights',
    featureFlagEnabled: (v: any) => !!v?.upsell,
    addonLinknameNormalized: 'softwarecostinsights',
    legacySubscriptionKeys: ['software_cost_insights', 'softwarecostinsights', 'sci'],
    reportRoute: '/software-cost-insights',
  }],
  ['screencasts', {
    name: 'screencasts',
    trackingName: 'Video Screen Recording',
    featureFlagKey: 'video-screen-recording-upsell-page',
    addonLinkname: 'VideoScreenRecording',
    legacySubscriptionKeys: ['video_screen_recording', 'videoscreenrecordingaddon'],
    reportRoute: '/screencasts',
  }],
  ['meeting-insights', {
    name: 'meeting-insights',
    trackingName: 'Meeting Insights',
    featureFlagKey: 'meeting-insights-upsell',
    addonLinkname: 'Meeting Insights',
    legacySubscriptionKeys: ['meeting_insights'],
    reportRoute: '/meeting-insights',
  }],
  ['web-app-usage', {
    name: 'web-app-usage',
    trackingName: 'Web & App Usage',
    featureFlagKey: 'web-and-app-usage:upsell',
    reportRoute: '/web-app-usage',
    postUpgradeCompanySettings: { webAndAppTracking: WebAppTrackingMode.Extended },
    variant: 'standard-self-serve',
    presentation: {
      toolbarTitleKey: 'productivity.title',
      titleKey: 'productivity.upsell.pageTitle',
      subtitleKey: 'productivity.upsell.pageSubTitle',
      learnMoreUrl: 'https://support.timedoctor.com/knowledge/the-web-app-usage-report',
      images: {
        desktop: '/assets/images/web-and-app-usage/upsell.svg',
        mobile: '/assets/images/web-and-app-usage/upsell-mobile.svg',
        desktopMinWidth: 760,
      },
      trackingPage: 'Web & App Usage Events',
      linkLocation: 'Web & App Usage Report Upsell Page',
      visitedPageKey: 'web-app-usage',
    },
  }],
  ['activity-summary', {
    name: 'activity-summary',
    trackingName: 'Activity Summary',
    featureFlagKey: 'activity-summary:upsell',
    reportRoute: '/activity-summary',
    variant: 'standard-self-serve',
    presentation: {
      toolbarTitleKey: 'activitySummary.title',
      titleKey: 'activitySummary.upsell.title',
      subtitleKey: 'activitySummary.upsell.subTitle',
      learnMoreUrl: 'https://support.timedoctor.com/knowledge/activity-summary-page',
      images: {
        desktop: '/assets/images/activity-summary/upsell.svg',
        mobile: '/assets/images/activity-summary/upsell-mobile.svg',
        desktopWidth: 820,
        desktopHeight: 230,
      },
      trackingPage: 'Activity Summary Events',
      linkLocation: 'Activity Summary Upsell Page',
      visitedPageKey: 'activity-summary',
    },
  }],
  ['dashboard-executive', {
    name: 'dashboard-executive',
    trackingName: 'Executive Dashboard',
    featureFlagKey: 'executive-dashboard-upsell-page',
    reportRoute: '/dashboard-executive',
  }],
  ['payroll', {
    name: 'payroll',
    trackingName: 'Payroll',
    featureFlagKey: 'payroll:upsell',
    reportRoute: '/payroll',
  }],
  ['schedules', {
    name: 'schedules',
    trackingName: 'Schedules',
    featureFlagKey: 'schedules:upsell',
    reportRoute: '/schedules',
    variant: 'standard-self-serve',
    presentation: {
      toolbarTitleKey: 'schedules.title',
      titleKey: 'schedules.upsell.title',
      subtitleKey: 'schedules.upsell.subTitle',
      learnMoreUrl: 'https://support.timedoctor.com/knowledge/how-to-set-up-work-schedules-in-time-doctor',
      images: {
        desktop: '/assets/images/schedules/upsell.svg',
        mobile: '/assets/images/schedules/upsell-mobile.png',
        desktopWidth: 855,
        desktopHeight: 262,
      },
      trackingPage: 'Work Schedules Events',
      linkLocation: 'Schedules Upsell Page',
      visitedPageKey: 'schedules',
    },
  }],
  ['attendance', {
    name: 'attendance',
    trackingName: 'Attendance',
    featureFlagKey: 'attendance:upsell',
    reportRoute: '/attendance',
    setupRoute: '/setup/attendance',
    variant: 'custom',
    presentation: {
      toolbarTitleKey: 'attendance.title',
      titleKey: 'attendance.upsell.title',
      subtitleKey: 'attendance.upsell.subTitle',
      learnMoreUrl: 'https://www.timedoctor.com/features/attendance',
      images: {
        desktop: '/assets/images/attendance/upsell.svg',
        mobile: '/assets/images/attendance/upsell-mobile.png',
        desktopWidth: 855,
        desktopHeight: 262,
      },
      setupImages: {
        desktop: '/assets/images/attendance/setup-attendance.svg',
      },
      setupTitleKey: 'attendance.upsell.setupTitle',
      setupSubtitleKey: 'attendance.upsell.setupSubTitle',
      setupCtaKey: 'attendance.upsell.createSchedule',
      setupCtaRoute: '/schedules',
      setupCtaTrackingName: 'Create Schedule',
      setupPageViewEvent: 'Attendance Setup Page Viewed',
      trackingPage: 'Attendance Events',
      linkLocation: 'Attendance Upsell Page',
      setupLinkLocation: 'Attendance Setup Page',
      visitedPageKey: 'attendance',
    },
  }],
  ['breaks', {
    name: 'breaks',
    trackingName: 'Breaks',
    featureFlagKey: 'breaks:upsell',
    reportRoute: '/breaks',
    variant: 'standard-self-serve',
    presentation: {
      toolbarTitleKey: 'breaks.title',
      titleKey: 'breaks.upsell.title',
      subtitleKey: 'breaks.upsell.subTitle',
      learnMoreUrl: 'https://support.timedoctor.com/knowledge/how-to-create-breaks-in-time-doctor',
      images: {
        desktop: '/assets/images/breaks/upsell.svg',
        mobile: '/assets/images/breaks/upsell-mobile.svg',
        desktopWidth: 928,
        desktopHeight: 272,
      },
      trackingPage: 'Break Events',
      linkLocation: 'Breaks Upsell Page',
      visitedPageKey: 'breaks',
    },
  }],
  ['productivity-ratings', {
    name: 'productivity-ratings',
    trackingName: 'Productivity Ratings',
    featureFlagKey: 'productivity-ratings:upsell',
    reportRoute: '/productivity-ratings',
    variant: 'standard-self-serve',
    presentation: {
      toolbarTitleKey: 'productivityRating.title',
      titleKey: 'productivityRating.upsell.title',
      subtitleKey: 'productivityRating.upsell.subTitle',
      learnMoreUrl: 'https://support.timedoctor.com/knowledge/productivity-ratings',
      images: {
        desktop: '/assets/images/productivity-ratings/upsell.svg',
        mobile: '/assets/images/productivity-ratings/upsell-mobile.svg',
        desktopMinWidth: 828,
      },
      trackingPage: 'Productivity Ratings Events',
      linkLocation: 'Productivity Ratings Upsell Page',
      visitedPageKey: 'productivity-ratings',
    },
  }],
  ['internet-connectivity', {
    name: 'internet-report',
    trackingName: 'Internet Connectivity',
    featureFlagKey: 'internet-connectivity:upsell',
    reportRoute: '/internet-report',
    upgradePlan: 'premium',
    postUpgradeCompanySettings: { trackConnectivity: true },
    variant: 'standard-self-serve',
    presentation: {
      toolbarTitleKey: 'connectivity.title',
      titleKey: 'connectivity.upsell.title',
      subtitleKey: 'connectivity.upsell.subTitle',
      learnMoreUrl: 'https://support.timedoctor.com/knowledge/what-reports-does-time-doctor-generate',
      images: {
        desktop: '/assets/images/internet-connectivity/upsell.svg',
        mobile: '/assets/images/internet-connectivity/upsell-mobile.svg',
        desktopMinWidth: 1025,
      },
      trackingPage: 'Internet Connectivity Events',
      linkLocation: 'Internet Connectivity Upsell Page',
      visitedPageKey: 'internet-connectivity',
    },
  }],
  ['approvals', {
    name: 'approvals',
    trackingName: 'Approvals',
    featureFlagKey: 'approvals:upsell',
    reportRoute: '/approvals',
    upgradePlan: 'standard',
    variant: 'custom',
    presentation: {
      toolbarTitleKey: 'approvals.title',
      titleKey: 'approvals.upsell.title',
      subtitleKey: 'approvals.upsell.subTitle',
      learnMoreUrl: 'https://support.timedoctor.com/knowledge/how-to-approve-or-disapprove-manual-time',
      images: {
        desktop: '/assets/images/approvals/upsell.svg',
        mobile: '/assets/images/approvals/upsell-mobile.svg',
        desktopMinWidth: 1024,
      },
      trackingPage: 'Manual Time Approval Events',
      linkLocation: 'Approvals Upsell Page',
      visitedPageKey: 'approvals',
    },
  }],
]);
